import type { Rect } from "@gooner/shared";

/** Relative-ish, human friendly timestamp for cards / save status. */
export function formatWhen(ms: number): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const now = Date.now();
  const diff = now - ms;
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return "just now";
  if (diff < hour) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date(now).getFullYear() ? undefined : "numeric",
  });
}

export const ASPECT_PRESETS: ReadonlyArray<{
  label: string;
  w: number;
  h: number;
  /** Which screen this shape fits — shown beside the ratio so the numbers mean something. */
  hint: string;
}> = [
  { label: "16:9", w: 16, h: 9, hint: "best for monitors & TVs" },
  { label: "9:16", w: 9, h: 16, hint: "best for phones" },
  { label: "4:3", w: 4, h: 3, hint: "best for tablets" },
  { label: "21:9", w: 21, h: 9, hint: "best for ultrawides" },
  { label: "1:1", w: 1, h: 1, hint: "square, splits evenly" },
];

export function aspectKey(a: { w: number; h: number }): string {
  return `${a.w}:${a.h}`;
}

/**
 * Two normalized coords are the same edge if they're within this. 1/3 does not round-trip
 * through a float, so exact comparison would break every 3-column layout.
 *
 * Calibrated against the editor's own geometry, not picked in a vacuum: `MIN_REGION` (the
 * smallest w/h `clampRect` allows, `client/src/control/lib/regionGeometry.ts`) is 0.04, four
 * times this tolerance — no legitimately distinct edge the editor can produce is ever this
 * close to another one. And `duplicateRegion`'s copy offset is 0.02, twice this tolerance,
 * so a duplicated-and-nudged region registers as its own distinct edge rather than merging
 * back into the original. The tolerance is wide enough to swallow float noise (~1e-16) and
 * narrow enough to never swallow anything the editor could actually produce on purpose.
 */
const EDGE_EPSILON = 0.01;

/**
 * The grid a view's regions form, as `"3×4"` (columns × rows), or null when they don't
 * form one.
 *
 * A grid means every region occupies exactly one cell of the lattice shared by all of
 * them, and every cell has exactly one region — a bijection between regions and cells, not
 * merely a matching count. Concretely: build the lattice from BOTH edges of every region
 * (x and x+w, y and y+h, each clustered within EDGE_EPSILON) rather than just their start
 * corner, then require each region to span exactly one pair of consecutive lattice lines
 * on each axis, and require no two regions to claim the same cell.
 *
 * Using only start corners (the earlier version of this function) is not enough: a
 * full-bleed hero underneath three small picture-in-picture overlays has 2 distinct
 * x-starts and 2 distinct y-starts — same as its region count — but it is a hero with
 * corner overlays, not a 2x2 grid, because the hero's own EXTENT covers the whole surface,
 * not one quarter of it. Extent has to be part of the check, or the card lies exactly the
 * way the spec warned it must not: confidently, over a hand-placed layout.
 *
 * Nor is "edges from both corners, product of counts matches" enough on its own once
 * overlap is possible: a full-bleed region plus one overlapping partial-width column can
 * make the aggregate edge count coincidentally equal the region count while the full-bleed
 * region plainly spans more than one resulting cell — so each region is checked
 * individually for a single-cell span. And even every region individually spanning one
 * cell isn't enough if two of them are allowed to span the *same* cell while another cell
 * goes unclaimed (a duplicate-on-top-of-a-gap): the same product, same per-region
 * alignment, still no grid. Only requiring a true one-to-one match between regions and
 * lattice cells rules all three of these out while still accepting every genuine grid.
 */
export function viewGrid(regions: ReadonlyArray<Rect>): string | null {
  if (regions.length === 0) return null;

  /** Sorted cluster representatives: values within EDGE_EPSILON of their neighbour merge. */
  const cluster = (values: number[]): number[] => {
    const sorted = [...values].sort((a, b) => a - b);
    const out: number[] = [];
    for (const v of sorted) {
      if (out.length === 0 || v - out[out.length - 1] > EDGE_EPSILON) out.push(v);
    }
    return out;
  };

  /** Which cluster (by index) a raw value belongs to, or -1 if none is within tolerance. */
  const clusterIndex = (clusters: number[], v: number): number =>
    clusters.findIndex((c) => Math.abs(v - c) <= EDGE_EPSILON);

  const xs = cluster(regions.flatMap((r) => [r.x, r.x + r.w]));
  const ys = cluster(regions.flatMap((r) => [r.y, r.y + r.h]));
  const cols = xs.length - 1;
  const rows = ys.length - 1;
  if (cols < 1 || rows < 1 || cols * rows !== regions.length) return null;

  const claimedCells = new Set<string>();
  for (const r of regions) {
    const xi = clusterIndex(xs, r.x);
    const xj = clusterIndex(xs, r.x + r.w);
    const yi = clusterIndex(ys, r.y);
    const yj = clusterIndex(ys, r.y + r.h);
    // A region that fits the grid spans exactly one pair of consecutive lattice lines on
    // each axis. xi/yi are never -1 here (xs/ys were built from these same values), so this
    // only trips when a region's extent skips over an intermediate line — i.e. spans more
    // than one cell.
    if (xj !== xi + 1 || yj !== yi + 1) return null;
    const cell = `${xi},${yi}`;
    // Two regions may not claim the same cell — that is overlap, and since the cell count
    // already equals the region count, a claimed-twice cell always implies an unclaimed one
    // elsewhere.
    if (claimedCells.has(cell)) return null;
    claimedCells.add(cell);
  }
  return `${cols}×${rows}`;
}

/**
 * Bytes as people read them — "256 MB", "1 GB", "4.5 GB". Whole megabytes; gigabytes to
 * one decimal when it isn't whole. The vault is the first thing on the site measured in
 * bytes (2026-09-09), so this is the first byte formatter; the wall's "25 MB" was a
 * literal at its call site.
 */
export function fmtBytes(bytes: number): string {
  const GB = 1024 ** 3;
  const MB = 1024 ** 2;
  if (bytes >= GB) {
    const g = bytes / GB;
    return `${Number.isInteger(g) ? g : g.toFixed(1)} GB`;
  }
  if (bytes >= MB) return `${Math.round(bytes / MB)} MB`;
  // Nothing is "0 MB", not "1 KB": the floor below is for a tiny FILE, which should never
  // read as empty, and an empty vault is not a tiny file.
  if (bytes <= 0) return "0 MB";
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
